import { AxiosError } from "axios";
import { PROVIDER_SELECT_QUERY } from "components/Page/PageFilter/ProviderFilter";
import useAuth from "hooks/useAuth";
import { Plan, PlanStatus } from "models/plan";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { getAllProviders } from "services/providerService";

export type FormFields = {
  id: string;
  name: string;
  description: string;
  status: PlanStatus;
  price: number;
  providerId: string;
  providerName: string;
};

export const useViewModel = (
  plan: Plan | undefined,
  onSubmit: ((fields: FormFields) => Promise<void>) | undefined
) => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm<FormFields>();
  const { data: providers } = useQuery(
    [PROVIDER_SELECT_QUERY, "provider-plans"],
    () => getAllProviders({}),
    {
      enabled: user?.role === "admin",
      refetchInterval: false,
      staleTime: Infinity,
    }
  );

  const { t } = useTranslation();
  const status = watch("status");
  const selectedProviderId = watch("providerId");

  useEffect(() => {
    if (plan) {
      setValue("name", plan.name);
      setValue("description", plan.description);
      setValue("status", plan.status);
      setValue("price", plan.price);
      setValue("providerId", plan.providerId);
    } else {
      setValue("status", "active");
      if (user && user.role === "provider" && user.provider?.id) {
        setValue("providerId", user.provider.id);
      }
    }
  }, [plan, setValue, user?._id]);

  useEffect(() => {
    register("providerId", {
      required: { value: true, message: "O provedor é obrigatório" },
    });
  }, [register]);

  const handleChangeProvider = (value: string | undefined) => {
    setValue("providerId", value || "");
  };

  async function handleOnSubmit(fields: FormFields) {
    if (onSubmit) {
      setLoading(true);
      try {
        await onSubmit({
          ...fields,
          price: Number(fields.price),
          status: fields.status,
        });
      } catch (err) {
        const _err = err as AxiosError;
        if (_err.isAxiosError) {
          if (_err.response?.status === 400) {
            toast.error("Erro de validação");
            return;
          }
        }
        toast("Ocorreu um erro ao salvar o plano", { type: "error" });
      } finally {
        setLoading(false);
      }
    }
  }

  return {
    t,
    loading,
    register,
    errors,
    handleSubmit: handleSubmit(handleOnSubmit),
    selectedProviderId,
    status,
    setValue,
    providers,
    handleChangeProvider,
    user,
  };
}; 